/**
 * Typed analytics wrapper.
 *
 * Single import surface for every track() call across Maren. Event
 * names and payload shapes are literal types so a typo fails at
 * compile time. Routes through posthog-js when configured; no-ops
 * cleanly when NEXT_PUBLIC_POSTHOG_KEY is unset.
 *
 * Why a wrapper:
 *  - Lazy-imports posthog-js so calling track() from a server-rendered
 *    component is harmless (the import resolves at runtime to nothing
 *    in that environment).
 *  - Centralises identify() / reset() so AuthContext doesn't import
 *    posthog-js directly.
 *  - Keeps the rest of the app blind to which analytics backend is in
 *    play. Swap providers by editing this file.
 */

// ── Event catalog (matches the onboarding plan) ────────────────────────

import type { MarketingVideoId } from "./marketingVideos";

export type AnalyticsEvent =
  | { name: "signup_complete"; props?: { method?: "email" | "google" } }
  | { name: "welcome_view"; props: { is_edit: boolean } }
  | { name: "welcome_submit"; props: { user_type: "first_time" | "retaker" } }
  | { name: "dashboard_view"; props: { first_time: boolean; walkthrough_active: boolean } }
  | {
      name: "log_question_submit";
      props: {
        count_after: number;
        walkthrough_step: "log_1" | "log_2" | "log_3" | null;
        used_sample_shortcut: boolean;
      };
    }
  | { name: "insight_seen"; props: { topic: string | null; distinct_topics: number } }
  | { name: "exam_date_set"; props: { days_until: number } }
  | {
      name: "chat_first_message_sent";
      props: { plan: "pro" | "max"; used_suggested_opener: boolean };
    }
  | { name: "locked_tile_click"; props: { tile_id: string } }
  | { name: "upgrade_complete"; props: { plan: "pro" | "max" } }
  | { name: "milestone_acknowledged"; props: { count: 25 | 50 | 100 } }
  | {
      name: "coachmark_action";
      props: {
        coachmark_id: string;
        // "silenced" added 2026-05-15 for the MilestonePopup's
        // permanent-dismiss path (distinct from inline dismiss).
        action: "shown" | "acted" | "dismissed" | "silenced" | "requeue";
      };
    }
  | { name: "nba_cta_click"; props: { step: string; done_count: number } }
  | { name: "nba_step_skip"; props: { step: string } }
  // Funnel events: homepage CTA attribution, pricing intent, and the
  // checkout step. Together they close the signup -> pricing -> checkout
  // -> paid funnel in PostHog (the DB already logs checkout_attempts; this
  // surfaces the same step in product analytics).
  | { name: "cta_click"; props: { location: string; plan?: "pro" | "max" } }
  | { name: "pricing_page_view"; props?: Record<string, never> }
  // `plan` widened to include study-pass targets (one-time passes flow
  // through the same checkout endpoint as the monthly tiers).
  | { name: "checkout_started"; props: { plan: "pro" | "max" | "pass_3mo" | "pass_6mo" } }
  // Founder-led funnel additions. `video_play` attributes which landing
  // video was started; `office_hours_register` marks intent to join a
  // session (the trust/community step between activation and checkout).
  | { name: "video_play"; props: { which: MarketingVideoId } }
  | { name: "office_hours_register"; props?: { source?: string } }
  // ── Free/paid restructure instrumentation (2026-07) ──────────────────
  // question_log_started fires once per form visit on first interaction;
  // milestones fire once per threshold ever (deduped via localStorage,
  // see logMilestones.ts). premium_* and upgrade_prompt_* carry enough
  // context to build the preview -> pricing -> checkout funnel.
  | { name: "question_log_started"; props: { count_before: number } }
  | { name: "first_question_logged"; props: { seconds_since_signup: number | null } }
  | { name: "question_log_milestone_reached"; props: { milestone: 1 | 5 | 10 | 20 | 30 | 50; plan: string } }
  | {
      name: "premium_insight_preview_shown";
      props: { surface: string; data_tier: string; pattern_count: number };
    }
  | { name: "premium_insight_preview_clicked"; props: { surface: string; pattern_count: number } }
  | { name: "premium_feature_blocked"; props: { feature: string; plan: string } }
  | { name: "upgrade_prompt_shown"; props: { location: string; trigger: string } }
  | { name: "upgrade_prompt_clicked"; props: { location: string; trigger: string } }
  | { name: "checkout_completed"; props: { plan: string } }
  | { name: "entitlement_activated"; props: { plan: string; source: "subscription" | "pass" } }
  | { name: "paid_insight_viewed"; props: { data_tier: string; pattern_count: number } }
  | { name: "paid_recommendation_completed"; props: { pattern_id: string } }
  // ── Entitlement-launch instrumentation (2026-07-19) ──────────────────
  // signup_started fires once per signup-page visit (top of the funnel;
  // signup_complete is the bottom). question_history_viewed marks the
  // /review record surface. free/paid_plan_cta_click split pricing
  // intent by tier. entitlement_expired fires once when a previously
  // paid account resolves to free (detected on plan fetch, deduped in
  // localStorage by setEntitlementContext).
  | { name: "signup_started"; props?: { plan?: string | null } }
  | { name: "question_history_viewed"; props: { questions: number } }
  | { name: "free_plan_cta_click"; props: { location: string } }
  | { name: "paid_plan_cta_click"; props: { location: string; plan: string } }
  | { name: "entitlement_expired"; props: { from_plan: string } }
  // ── Onboarding diagnostic funnel (2026-08) ───────────────────────────
  // EVERY event carries user_state so cold acquisition and dormant-user
  // reactivation never share a denominator. 86 of 132 accounts have never
  // logged a question, so old accounts coming back would otherwise inflate
  // the same rate and make the release look like a win while saying
  // nothing about the new funnel.
  //
  //   anonymous          no session; a cold visitor, usually from social
  //   existing_inactive  signed in, has NEVER logged a real question
  //   existing_active    signed in and already activated
  //
  // diagnostic_version pins which question set produced the numbers, so
  // editing the questions cannot silently pollute a running comparison.
  | { name: "diagnostic_view"; props: DiagnosticProps }
  | { name: "diagnostic_started"; props: DiagnosticProps & { resumed: boolean } }
  | {
      name: "diagnostic_question_answered";
      props: DiagnosticProps & {
        question_id: string;
        position: number;
        correct: boolean;
        // A correct answer they were guessing at. Reported separately
        // because it is NOT a miss; conflating them would corrupt the
        // meaning of "missed" everywhere else in the product.
        lucky_guess: boolean;
        reason_count: number;
        seconds: number | null;
      };
    }
  | {
      name: "diagnostic_completed";
      props: DiagnosticProps & {
        correct: number;
        misses: number;
        lucky_guesses: number;
      };
    }
  | { name: "diagnostic_path_chosen"; props: DiagnosticProps & { path: "free" | "guided" } }
  | {
      name: "diagnostic_setup_completed";
      props: DiagnosticProps & {
        weeks_until_exam: number | null;
        user_type: "first_time" | "retaker";
        resource_count: number;
        weekly_hours: number;
      };
    }
  | {
      name: "diagnostic_plan_viewed";
      props: DiagnosticProps & { recommended_plan: string; weeks_until_exam: number | null };
    }
  | { name: "diagnostic_saved"; props: DiagnosticProps & { saved: number; replaced: number } }
  // ── Saved explanations (2026-09) ─────────────────────────────────────
  // requested -> attached is the guarantee ("you will not lose an
  // explanation you paid for"); attach_failed is the number to watch.
  // reopened and flagged are the usefulness signals: does anyone come
  // back to a saved explanation later, and does it look wrong to them.
  | {
      name: "explanation_requested";
      props: { surface: "log" | "review"; plan: string; result_type: string | null };
    }
  | { name: "explanation_attached"; props: { surface: "log" | "review" } }
  | {
      name: "explanation_attach_failed";
      props: { surface: "log" | "review"; reason: "no_use_id" | "not_attached" | "request_failed" };
    }
  | { name: "explanation_reopened"; props: { age_days: number } }
  | { name: "explanation_flagged"; props: { surface: "log" | "review" } };

/** The split that keeps acquisition and reactivation apart. */
export type DiagnosticUserState = "anonymous" | "existing_inactive" | "existing_active";

/** Attached to every diagnostic funnel event. */
export interface DiagnosticProps {
  user_state: DiagnosticUserState;
  diagnostic_version: string;
}

// ── Public API ─────────────────────────────────────────────────────────

export function track(event: AnalyticsEvent): void {
  if (typeof window === "undefined") return;
  if (!process.env.NEXT_PUBLIC_POSTHOG_KEY) return;
  void getPosthog().then((ph) => {
    if (!ph) return;
    ph.capture(event.name, "props" in event ? (event.props as Record<string, unknown>) : undefined);
  });
}

export function identify(userId: string): void {
  if (typeof window === "undefined") return;
  if (!process.env.NEXT_PUBLIC_POSTHOG_KEY) return;
  // Per privacy commitment: only the Supabase UUID. No email, no name.
  void getPosthog().then((ph) => ph?.identify(userId));
}

export function reset(): void {
  if (typeof window === "undefined") return;
  if (!process.env.NEXT_PUBLIC_POSTHOG_KEY) return;
  void getPosthog().then((ph) => ph?.reset());
}

// localStorage key for entitlement_expired dedup. Stores the last
// effective plan this browser saw for the signed-in user.
const _LAST_PLAN_KEY = "maren_last_effective_plan";

/**
 * Attach the resolved entitlement to the PostHog person so cohorts can
 * segment by current access. Called wherever /billing/plan is fetched.
 *
 * Privacy: only the effective tier and the grandfathered boolean --
 * never Stripe ids, prices, or billing status. Also detects the
 * paid -> free transition and fires entitlement_expired exactly once
 * per downgrade (localStorage-deduped per browser).
 */
export function setEntitlementContext(plan: string, grandfathered?: boolean): void {
  if (typeof window === "undefined") return;
  if (!process.env.NEXT_PUBLIC_POSTHOG_KEY) return;
  try {
    const prev = window.localStorage.getItem(_LAST_PLAN_KEY);
    if ((prev === "pro" || prev === "max") && plan === "free") {
      track({ name: "entitlement_expired", props: { from_plan: prev } });
    }
    window.localStorage.setItem(_LAST_PLAN_KEY, plan);
  } catch {
    // Storage unavailable (private mode): still set the person props.
  }
  void getPosthog().then((ph) => {
    ph?.setPersonProperties({
      effective_plan: plan,
      ...(grandfathered === undefined ? {} : { grandfathered }),
    });
  });
}

// ── Internals ──────────────────────────────────────────────────────────

// Cached import promise so we don't re-import on every track() call.
let _phPromise: Promise<typeof import("posthog-js").default | null> | null = null;

function getPosthog(): Promise<typeof import("posthog-js").default | null> {
  if (!_phPromise) {
    _phPromise = import("posthog-js")
      .then(({ default: posthog }) => {
        // PostHogProvider initialized it; if it hasn't loaded yet,
        // posthog-js queues the calls and replays them on init.
        return posthog;
      })
      .catch(() => null);
  }
  return _phPromise;
}
